/**
 * YogiQL's chart specs, as the client places a panel into them: each
 * chart's metric and breakdown positions, outer to inner. Copied from the
 * backend chart registry (YogiQL's `charts/value.py`) rather than fetched:
 * a slot change there needs the same change here.
 */

/** `min` to `max` positions of one list, and the role they draw. */
export interface YogiQLSlot {
  role: string
  min: number
  max: number
}

/** Required slots always fill; optional ones take the surplus left to right. */
export interface YogiQLChartSpec {
  name: YogiQLChartName
  metrics: YogiQLSlot[]
  breakdowns: YogiQLSlot[]
}

export type YogiQLChartName =
  | 'bar'
  | 'line'
  | 'scatter'
  | 'bubble'
  | 'stacked_bar'
  | 'pie'
  | 'heatmap'
  | 'treemap'
  | 'table'
  | 'ridgeline'
  | 'sankey'

const slot = (role: string, min: number, max: number): YogiQLSlot => ({
  role,
  min,
  max,
})

/**
 * Registry of YogiQL chart specs by chart name
 */
export const YOGIQL_CHART_SPECS: Record<YogiQLChartName, YogiQLChartSpec> = {
  bar: {
    name: 'bar',
    metrics: [slot('Metric', 1, 1)],
    breakdowns: [slot('Breakdown', 0, 1)],
  },
  line: {
    name: 'line',
    metrics: [slot('Metrics', 1, 2)],
    breakdowns: [slot('Series', 0, 1), slot('Time axis', 1, 1)],
  },
  scatter: {
    name: 'scatter',
    metrics: [slot('X axis', 1, 1), slot('Y axis', 1, 1)],
    breakdowns: [slot('Breakdown', 0, 1)],
  },
  bubble: {
    name: 'bubble',
    metrics: [
      slot('X axis', 1, 1),
      slot('Y axis', 1, 1),
      slot('Bubble size', 0, 1),
    ],
    breakdowns: [slot('Breakdown', 0, 1)],
  },
  stacked_bar: {
    name: 'stacked_bar',
    metrics: [slot('Metric', 1, 1)],
    breakdowns: [slot('Bars', 0, 1), slot('Segments', 0, 1)],
  },
  pie: {
    name: 'pie',
    metrics: [slot('Metric', 1, 1)],
    breakdowns: [slot('Slices', 0, 1)],
  },
  heatmap: {
    name: 'heatmap',
    metrics: [slot('Cell color', 1, 1), slot('Cell label', 0, 1)],
    breakdowns: [slot('Y axis', 1, 1), slot('X axis', 1, 1)],
  },
  treemap: {
    name: 'treemap',
    metrics: [
      slot('Box size', 1, 1),
      slot('Box color', 0, 1),
      slot('Label', 0, 2),
    ],
    breakdowns: [slot('Groups', 0, 1), slot('Boxes', 1, 1)],
  },
  table: {
    name: 'table',
    metrics: [slot('Metrics', 1, 10)],
    breakdowns: [slot('Rows', 0, 5)],
  },
  ridgeline: {
    name: 'ridgeline',
    metrics: [slot('Count', 1, 1), slot('Score average', 1, 1)],
    breakdowns: [slot('Series', 0, 1), slot('Bins', 1, 1)],
  },
  sankey: {
    name: 'sankey',
    metrics: [slot('Width', 1, 1), slot('Color', 1, 1)],
    breakdowns: [slot('Columns', 1, 6)],
  },
}
